/**
 * Override one IDL message or one control of a saved MCE adapter. The browser
 * checks the edit with the converter, then the server rebuilds and stores it.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Radio, Select, Table } from "antd";
import { base64ToBytes, bytesToBase64 } from "../../../../utils/base64";
import GETJwtToken from "../../../../redux/authentication/authentication";
import {
  declaredCommands,
  MceDeclaredCommands,
  readOverrides,
  rebuildWithOverrides,
} from "../../../../utils/mceIdl/client";
import {
  memberOptions,
  MceEventClass,
  pickerValueToTemplate,
} from "../../../../utils/mceIdl/eventClasses";
import { asString, unpackMapping } from "../../../../utils/mceIdl/messages";
import { downloadMceIdlLog } from "../../../../utils/mceIdl/mceIdlLog";
import {
  ACTIVE_WHEN_OPTIONS,
  byteOrderOfRow,
  draftsIdentity,
  draftsOfRow,
  draftsToTargets,
  isDraftComplete,
  isOneShot,
  MceCommandDraft,
  deviceOfProduct,
  IGNORE_REASON_OPTIONS,
  MceControlEdit,
  MceEntryEdit,
  withControlOverride,
  withEntryOverride,
} from "../../../../utils/mceIdl/overrides";
import { MceSaveFailure, saveUploadedMapping } from "../../../../utils/mceIdl/saveUpload";
import {
  diagnosticRows,
  MceControlRow,
  MceDiagnosticRow,
  MceMessageRow,
  MceSourceNames,
} from "../../../../utils/mceIdl/tableView";
import MceCommandEditor from "./MceCommandEditor";

const COMMANDS_NOT_LISTED =
  "This version of the converter does not list the commands in the IDL, so a control cannot be sent to a different command yet. You can still stop sending it.";

const COMMANDS_CUT_OFF =
  "This IDL declares more commands than can be listed, so some are missing from the list.";

const COLUMNS = [
  { title: "Code", dataIndex: "code", width: 180 },
  { title: "Message", dataIndex: "message" },
];
const COULD_NOT_CHECK =
  "The change could not be checked. Reload the page and try again. If this continues, click Get log and send the file to technical support.";
const CHANGE_REFUSED = "This change cannot be used. Fix the errors below and try again.";

/** What is being overridden. */
export type MceOverrideTarget =
  { kind: "message"; row: MceMessageRow } | { kind: "control"; row: MceControlRow };

interface MceOverrideEditorProps {
  target: MceOverrideTarget;
  adapterId: string;
  cdoBinaryBase64: string;
  names: MceSourceNames;
  onClose: () => void;
  onSaved: () => void;
}

export default function MceOverrideEditor({
  target,
  adapterId,
  cdoBinaryBase64,
  names,
  onClose,
  onSaved,
}: MceOverrideEditorProps) {
  const isMessage = target.kind === "message";
  const [mode, setMode] = useState<"ignore" | "flag" | "commands">("ignore");
  const [declared, setDeclared] = useState<MceDeclaredCommands | null>(null);
  const [drafts, setDrafts] = useState<MceCommandDraft[]>([]);
  const [savedDrafts, setSavedDrafts] = useState("");
  const [reason, setReason] = useState<number | null>(null);
  const [eventClass, setEventClass] = useState<MceEventClass>("status");
  const [member, setMember] = useState<number | null>(null);
  const [activeWhen, setActiveWhen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [findings, setFindings] = useState<MceDiagnosticRow[]>([]);
  const [failure, setFailure] = useState<MceSaveFailure | null>(null);

  useEffect(() => {
    if (target.kind !== "control") return undefined;
    let current = true;
    const mapping = unpackMapping(base64ToBytes(cdoBinaryBase64));
    readOverrides(mapping)
      .then((overrides) => declaredCommands(mapping, overrides))
      .then((listing) => {
        if (!current) return;
        setDeclared(listing);
        const existing = draftsOfRow(
          target.row.row,
          listing.commands.filter((one) => !listing.unmatchable.has(asString(one.name))),
        );
        setDrafts(existing);
        setSavedDrafts(draftsIdentity(existing));
        if (existing.length > 0) setMode("commands");
      })
      .catch((error) => {
        if (current) setFailure({ message: COULD_NOT_CHECK, detail: `listing: ${String(error)}` });
      });
    return () => {
      current = false;
    };
  }, [target, cdoBinaryBase64]);

  const editOf = (): MceEntryEdit | MceControlEdit | null => {
    if (mode === "commands") {
      if (
        target.kind !== "control" ||
        drafts.length === 0 ||
        !drafts.every(isDraftComplete) ||
        draftsIdentity(drafts) === savedDrafts
      ) {
        return null;
      }
      return {
        kind: "commands",
        commands: draftsToTargets(drafts, byteOrderOfRow(target.row.row)),
        oneShot: isOneShot(target.row.row),
      };
    }
    if (mode === "ignore") return reason === null ? null : { kind: "ignore", reason };
    if (member === null || activeWhen === null) return null;
    const mapping = unpackMapping(base64ToBytes(cdoBinaryBase64));
    const template = pickerValueToTemplate(
      eventClass,
      member,
      deviceOfProduct(mapping.productType),
    );
    return template ? { kind: "flag", template, activeWhen } : null;
  };

  /** Build the document with this change, check it, and store it. Null removes the override. */
  const save = async (edit: MceEntryEdit | MceControlEdit | null) => {
    setBusy(true);
    setFindings([]);
    setFailure(null);
    try {
      const mapping = unpackMapping(base64ToBytes(cdoBinaryBase64));
      const current = await readOverrides(mapping);
      let next = current;
      if (target.kind === "message" && target.row.entryKey) {
        next = withEntryOverride(current, target.row.entryKey, edit as MceEntryEdit | null);
      } else if (target.kind === "control" && target.row.template) {
        next = withControlOverride(
          current,
          target.row.row,
          target.row.template,
          edit as MceControlEdit | null,
        );
      }
      const result = await rebuildWithOverrides(mapping, next);
      if (result.cdoBinary.length === 0) {
        setFindings(diagnosticRows(result.diagnostics, names).filter((row) => row.isError));
        return;
      }
      const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
      const refused = await saveUploadedMapping(
        `${apiBase}/adapters/${adapterId}/cdo`,
        await GETJwtToken(),
        bytesToBase64(result.cdoBinary),
        cdoBinaryBase64,
      );
      if (refused) {
        setFailure(refused);
        return;
      }
      onSaved();
      onClose();
    } catch (error) {
      setFailure({ message: COULD_NOT_CHECK, detail: `override: ${String(error)}` });
    } finally {
      setBusy(false);
    }
  };

  const { row } = target;
  return (
    <Modal
      open
      title={`Override ${isMessage ? (row as MceMessageRow).name : (row as MceControlRow).control?.member}`}
      okText="Save"
      okButtonProps={{ disabled: editOf() === null, loading: busy }}
      onOk={() => save(editOf())}
      onCancel={onClose}
    >
      {row.override && (
        <Button className="mb-3" disabled={busy} onClick={() => save(null)}>
          Reset to default
        </Button>
      )}
      {isMessage && (
        <Radio.Group
          className="mb-3 d-block"
          value={mode}
          onChange={(event) => setMode(event.target.value)}
        >
          <Radio value="ignore">Do not send to LN2</Radio>
          <Radio value="flag">Send one LN2 member</Radio>
        </Radio.Group>
      )}
      {!isMessage && declared !== null && declared.commands.length === 0 && (
        <Alert className="mb-3" type="info" showIcon message={COMMANDS_NOT_LISTED} />
      )}
      {!isMessage && declared !== null && declared.commands.length > 0 && (
        <Radio.Group
          className="mb-3 d-block"
          aria-label="What this control does"
          value={mode}
          onChange={(event) => setMode(event.target.value)}
        >
          <Radio value="commands">Send these commands</Radio>
          <Radio value="ignore">Do not send anything</Radio>
        </Radio.Group>
      )}
      {mode === "commands" && (
        <>
          {declared?.cutOff && (
            <Alert className="mb-3" type="warning" showIcon message={COMMANDS_CUT_OFF} />
          )}
          <MceCommandEditor
            declared={declared?.commands ?? []}
            unmatchable={declared?.unmatchable ?? new Set()}
            drafts={drafts}
            onChange={setDrafts}
          />
        </>
      )}
      {mode === "ignore" && (
        <Select
          className="generalStyles__width100"
          aria-label="Reason"
          placeholder="Choose a reason"
          value={reason ?? undefined}
          onChange={setReason}
          options={IGNORE_REASON_OPTIONS}
        />
      )}
      {mode === "flag" && (
        <>
          <Select
            className="generalStyles__width100 mb-2"
            aria-label="Class"
            value={eventClass}
            onChange={(value: MceEventClass) => {
              setEventClass(value);
              setMember(null);
            }}
            options={[
              { value: "status", label: "Status" },
              { value: "fault", label: "Fault" },
              { value: "service", label: "Service" },
            ]}
          />
          <Select
            showSearch
            className="generalStyles__width100 mb-2"
            aria-label="Member"
            placeholder="Choose a member"
            optionFilterProp="label"
            value={member ?? undefined}
            onChange={setMember}
            options={memberOptions(eventClass)}
          />
          <Select
            className="generalStyles__width100"
            aria-label="Active when"
            placeholder="Active when"
            value={activeWhen ?? undefined}
            onChange={setActiveWhen}
            options={ACTIVE_WHEN_OPTIONS}
          />
        </>
      )}
      {findings.length > 0 && (
        <>
          <Alert className="mt-3" type="error" showIcon message={CHANGE_REFUSED} />
          <Table
            className="mt-2"
            size="small"
            rowKey="key"
            columns={COLUMNS}
            dataSource={findings}
            pagination={false}
          />
        </>
      )}
      {failure && (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message={failure.message}
          action={
            <Button
              size="small"
              onClick={() =>
                downloadMceIdlLog({
                  when: new Date(),
                  action: "override",
                  product: "",
                  files: [],
                  error: new Error(failure.detail),
                })
              }
            >
              Get log
            </Button>
          }
        />
      )}
    </Modal>
  );
}
